import { isGiphyMediaSrc } from '../../components/RichText';

/**
 * GIF search for the comment box (Messaging Drop 3b).
 *
 * The browser calls GIPHY directly with a public key read from
 * VITE_GIPHY_API_KEY at build time. Like every VITE_ value it ends up in the
 * bundle (README "Environment variables"); a GIPHY key is a public client key
 * made for exactly this. No key in the build means no GIF button at all:
 * giphyApiKey() returns null and CommentEditor does not render the button.
 *
 * GIFs are never uploaded or copied. The comment stores a link to GIPHY's
 * media host, and the comment sanitizer admits GIPHY's media hosts only
 * (isAllowedCommentImageSrc in components/RichText.tsx).
 */

const SEARCH_URL = 'https://api.giphy.com/v1/gifs/search';

/** Enough to choose from without paging; each search is one API call. */
export const GIF_RESULT_LIMIT = 24;

/** The public GIPHY key, or null when the build has none (or only spaces). */
export function giphyApiKey(): string | null {
  const value = import.meta.env.VITE_GIPHY_API_KEY;
  if (value === undefined) {
    return null;
  }
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

export interface GifResult {
  id: string;
  title: string;
  /** Small rendition, for the picker grid only. */
  previewUrl: string;
  /** What goes into the comment. */
  url: string;
}

/**
 * A GIPHY media URL made safe to store: https, a GIPHY media host, and no
 * query string or fragment (GIPHY appends tracking ids such as ?cid=...; the
 * image itself does not need them). Anything else is null and the result is
 * dropped, so the picker can never insert an image the sanitizer would strip.
 */
export function cleanGiphyUrl(raw: unknown): string | null {
  if (typeof raw !== 'string' || !isGiphyMediaSrc(raw)) {
    return null;
  }
  const url = new URL(raw);
  return `${url.origin}${url.pathname}`;
}

interface GiphyImage {
  url?: unknown;
}

interface GiphyGif {
  id?: unknown;
  title?: unknown;
  images?: Record<string, GiphyImage | undefined>;
}

/** Turns GIPHY's search response into results the picker can show. */
export function parseGifResults(payload: unknown): GifResult[] {
  const data = (payload as { data?: unknown } | null)?.data;
  if (!Array.isArray(data)) {
    return [];
  }
  const results: GifResult[] = [];
  for (const raw of data as unknown[]) {
    // One malformed entry (null, a string) must not throw away the whole page.
    if (typeof raw !== 'object' || raw === null) {
      continue;
    }
    const item = raw as GiphyGif;
    const images = item.images ?? {};
    const url = cleanGiphyUrl(images['fixed_height']?.url);
    const previewUrl =
      cleanGiphyUrl(images['fixed_height_small']?.url) ??
      cleanGiphyUrl(images['fixed_width_small']?.url) ??
      url;
    if (url === null || previewUrl === null) {
      continue;
    }
    results.push({
      id: typeof item.id === 'string' ? item.id : url,
      title: typeof item.title === 'string' && item.title.trim() !== '' ? item.title : 'GIF',
      previewUrl,
      url,
    });
  }
  return results;
}

/** The request URL for one search. Rating "g" only. */
export function gifSearchUrl(apiKey: string, query: string): string {
  const params = new URLSearchParams({
    api_key: apiKey,
    q: query,
    limit: String(GIF_RESULT_LIMIT),
    rating: 'g',
    lang: 'en',
  });
  return `${SEARCH_URL}?${params.toString()}`;
}

export const GIF_SEARCH_FAILED = 'GIF search is not working right now. Try again later.';

/** One GIPHY search. Throws with a plain sentence when GIPHY refuses. */
export async function searchGifs(
  apiKey: string,
  query: string,
  signal?: AbortSignal,
  fetchImpl: typeof fetch = fetch,
): Promise<GifResult[]> {
  const response = await fetchImpl(gifSearchUrl(apiKey, query), {
    signal: signal ?? null,
    // No cookies or referrer detail go to GIPHY with the search.
    credentials: 'omit',
    referrerPolicy: 'no-referrer',
  });
  if (!response.ok) {
    throw new Error(GIF_SEARCH_FAILED);
  }
  return parseGifResults(await response.json());
}
